import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, LoadingSpinner, Text } from '../../components/ui'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { contratosService, getOpenAmountsTotal } from '../../services/contratos'
import { marketingNotasService } from '../../services/marketingNotas'
import { marketingSolicitacaoService } from '../../services/marketingSolicitacao'
import { supplierQuotesService } from '../../services/supplierQuotes'
import type { Contract } from '../../types/contratos'
import type { MarketingInvoice } from '../../types/marketingNotas'
import type {
  MarketingRequest,
  MarketingRequestComment,
  MarketingRequestHistory,
  MarketingRequestStage,
} from '../../types/marketingSolicitacao'
import type { SupplierQuote } from '../../types/supplierQuotes'
import {
  documentTypeLabel,
  formatCurrency,
  invoiceTotal,
  statusLabel as invoiceStatusLabel,
} from './notasUi'
import {
  DEFAULT_STAGE_COLOR,
  ITEM_TYPE_LABELS,
  MODULE_NAME,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  formatDateOnlyPtBr,
  formatDateTimePtBr,
  formatRequesterWithDepartment,
  isMarketingRequestPriority,
  isRequestClosed,
} from './solicitacaoUi'
import {
  formatQuoteNumber,
  quoteTotal,
  statusLabel as quoteStatusLabel,
} from './propostasUi'

function contractStatusLabel(status: string | null | undefined): string {
  return status === 'confirmed' ? 'Confirmado' : 'Rascunho'
}

type Props = {
  request: MarketingRequest | null
  stages: MarketingRequestStage[]
  onClose: () => void
  onEdit: (request: MarketingRequest) => void
  onDelete: (requestId: string) => void | Promise<void>
}

const DETAIL_LABELS: Record<string, string> = {
  item_id: 'ID do item',
  item: 'Item',
  quantidade: 'Quantidade',
  destinatario: 'Destinatário',
  finalidade: 'Finalidade',
  endereco: 'Endereço',
  personalizacao: 'Personalização',
  formato: 'Formato',
  onde_sera_veiculada: 'Onde será veiculada',
  medidas: 'Medidas',
  texto_base: 'Texto base',
  referencias: 'Referências',
  data: 'Data',
  local: 'Local',
  publico_estimado: 'Público estimado',
  o_que_marketing_fornece: 'O que o marketing fornece',
  responsavel_no_local: 'Responsável no local',
  material: 'Material',
  prazo: 'Prazo',
  destino_fisico: 'Destino físico',
  canal: 'Canal',
  mensagem_principal: 'Mensagem principal',
  publico: 'Público',
  data_veiculacao: 'Data de veiculação',
  descricao: 'Descrição',
  justificativa: 'Justificativa',
}

const FIELD_LABELS: Record<string, string> = {
  stage_id: 'Etapa',
  handler_id: 'Responsável',
  title: 'Título',
  notes: 'Observação geral',
  needed_on: 'Para quando',
  priority: 'Prioridade',
  closed_at: 'Finalização',
  deleted_at: 'Exclusão',
  comment: 'Comentário',
  comment_deleted: 'Comentário excluído',
}

type HistoryLookupCtx = {
  stages: MarketingRequestStage[]
  profilesById: Map<string, string>
}

function formatDetailValue(_key: string, value: unknown): string {
  if (value == null || value === '') return '—'
  return String(value)
}

function isImageAttachment(mimeType: string | null | undefined, fileName: string): boolean {
  if (mimeType?.startsWith('image/')) return true
  return /\.(jpe?g|png|gif|webp)$/i.test(fileName)
}

function formatHistoryValue(fieldChanged: string, value: string | null, ctx: HistoryLookupCtx): string {
  if (value == null || value === '') {
    if (fieldChanged === 'deleted_at') return 'Ativa'
    if (fieldChanged === 'comment_deleted') return 'Excluído'
    return '—'
  }
  switch (fieldChanged) {
    case 'stage_id': {
      const stage = ctx.stages.find((s) => s.id === value)
      return stage?.name ?? 'Etapa indisponível'
    }
    case 'handler_id': {
      return ctx.profilesById.get(value) ?? 'Usuário indisponível'
    }
    case 'deleted_at':
      return 'Excluída'
    case 'title':
    case 'notes':
    case 'comment':
      return value
    case 'comment_deleted':
      return value || '—'
    case 'needed_on':
      return formatDateOnlyPtBr(value)
    case 'priority':
      return isMarketingRequestPriority(value) ? PRIORITY_LABELS[value] : value
    case 'closed_at':
      return formatDateTimePtBr(value)
    default:
      return value
  }
}

function isCommentEdited(comment: MarketingRequestComment): boolean {
  if (!comment.updated_at) return false
  const created = new Date(comment.created_at).getTime()
  const updated = new Date(comment.updated_at).getTime()
  return Number.isFinite(created) && Number.isFinite(updated) && updated > created + 1000
}

const SolicitacaoDetailDrawer: React.FC<Props> = ({
  request,
  stages,
  onClose,
  onEdit,
  onDelete,
}) => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canUpdate, loading: updatePermLoading } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete, loading: deletePermLoading } = usePermission(MODULE_NAME, 'delete')
  const { hasPermission: canViewValues, loading: viewValuesLoading } = usePermission(
    MODULE_NAME,
    'view_values'
  )
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const [error, setError] = useState<string | null>(null)
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({})

  const [comments, setComments] = useState<MarketingRequestComment[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingCommentDraft, setEditingCommentDraft] = useState('')
  const [savingCommentEdit, setSavingCommentEdit] = useState(false)

  const [history, setHistory] = useState<MarketingRequestHistory[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyProfilesById, setHistoryProfilesById] = useState<Map<string, string>>(new Map())

  const [linkedQuotes, setLinkedQuotes] = useState<SupplierQuote[]>([])
  const [quotesLoading, setQuotesLoading] = useState(false)
  const [linkedInvoices, setLinkedInvoices] = useState<MarketingInvoice[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const [linkedContracts, setLinkedContracts] = useState<Contract[]>([])
  const [contractsLoading, setContractsLoading] = useState(false)

  const canSeeLinkedDocs = !viewValuesLoading && canViewValues
  const requestClosed = isRequestClosed(request, stages)
  const canEditRequest =
    !updatePermLoading && canUpdate && !request?.deleted_at && !requestClosed
  const canDeleteRequest =
    !deletePermLoading && canDelete && !request?.deleted_at && !requestClosed

  const loadComments = useCallback(async (requestId: string) => {
    setCommentsLoading(true)
    try {
      const list = await marketingSolicitacaoService.listComments(requestId)
      setComments(list)
    } catch {
      setComments([])
    } finally {
      setCommentsLoading(false)
    }
  }, [])

  const loadHistory = useCallback(async (requestId: string) => {
    setHistoryLoading(true)
    try {
      const list = await marketingSolicitacaoService.listHistory(requestId)
      setHistory(list)

      const handlerIds = list
        .filter((h) => h.field_changed === 'handler_id')
        .flatMap((h) => [h.old_value, h.new_value])
        .filter((id): id is string => Boolean(id))

      if (handlerIds.length > 0) {
        const profileRows = await marketingSolicitacaoService.listProfilesByIds(handlerIds)
        const map = new Map<string, string>()
        for (const p of profileRows) {
          map.set(p.id, (p.name || '').trim() || p.id)
        }
        setHistoryProfilesById(map)
      } else {
        setHistoryProfilesById(new Map())
      }
    } catch {
      setHistory([])
      setHistoryProfilesById(new Map())
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!request) {
      setComments([])
      setHistory([])
      setHistoryProfilesById(new Map())
      setLinkedQuotes([])
      setLinkedInvoices([])
      setLinkedContracts([])
      setNewComment('')
      setEditingCommentId(null)
      setEditingCommentDraft('')
      setError(null)
      return
    }
    setEditingCommentId(null)
    setEditingCommentDraft('')
    void loadComments(request.id)
  }, [request?.id, loadComments])

  useEffect(() => {
    if (!request?.id || !canSeeLinkedDocs) {
      setLinkedQuotes([])
      setQuotesLoading(false)
      return
    }
    let cancelled = false
    const load = async () => {
      setQuotesLoading(true)
      try {
        const list = await supplierQuotesService.listByRequest(request.id)
        if (!cancelled) setLinkedQuotes(list)
      } catch {
        if (!cancelled) setLinkedQuotes([])
      } finally {
        if (!cancelled) setQuotesLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [request?.id, canSeeLinkedDocs])

  useEffect(() => {
    if (!request?.id || !canSeeLinkedDocs) {
      setLinkedInvoices([])
      setInvoicesLoading(false)
      return
    }
    let cancelled = false
    const load = async () => {
      setInvoicesLoading(true)
      try {
        const list = await marketingNotasService.listByRequest(request.id)
        if (!cancelled) setLinkedInvoices(list)
      } catch {
        if (!cancelled) setLinkedInvoices([])
      } finally {
        if (!cancelled) setInvoicesLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [request?.id, canSeeLinkedDocs])

  useEffect(() => {
    if (!request?.id || !canSeeLinkedDocs) {
      setLinkedContracts([])
      setContractsLoading(false)
      return
    }
    let cancelled = false
    const load = async () => {
      setContractsLoading(true)
      try {
        const list = await contratosService.listByRequest(request.id)
        if (!cancelled) setLinkedContracts(list)
      } catch {
        if (!cancelled) setLinkedContracts([])
      } finally {
        if (!cancelled) setContractsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [request?.id, canSeeLinkedDocs])

  useEffect(() => {
    if (!request?.id) return
    void loadHistory(request.id)
  }, [
    request?.id,
    request?.stage_id,
    request?.handler_id,
    request?.priority,
    request?.updated_at,
    loadHistory,
  ])

  useEffect(() => {
    if (!request?.attachments?.length) {
      setSignedUrls({})
      return
    }
    let cancelled = false
    const load = async () => {
      const entries = await Promise.all(
        request.attachments!.map(async (att) => {
          const url = await marketingSolicitacaoService.getAttachmentUrl(att.file_path)
          return [att.id, url] as const
        })
      )
      if (cancelled) return
      const map: Record<string, string> = {}
      for (const [id, url] of entries) {
        if (url) map[id] = url
      }
      setSignedUrls(map)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [request?.id, request?.attachments])

  const historyCtx: HistoryLookupCtx = useMemo(
    () => ({
      stages,
      profilesById: historyProfilesById,
    }),
    [stages, historyProfilesById]
  )

  if (!request) return null

  const currentUserId = user?.id
  const attachments = request.attachments || []

  const submitComment = async () => {
    const content = newComment.trim()
    if (!content) return
    setSubmittingComment(true)
    setError(null)
    try {
      const added = await marketingSolicitacaoService.addComment(request.id, content)
      setComments((prev) => [...prev, added])
      setNewComment('')
      textareaRef.current?.focus()
      void logCreate(
        'marketing_request',
        request.id,
        `Comentou na solicitação de marketing N° ${request.request_number}`,
        {
          comment_id: added.id,
          content_preview: content.slice(0, 200),
          request_number: request.request_number,
        }
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar comentário.')
    } finally {
      setSubmittingComment(false)
    }
  }

  const startEditComment = (comment: MarketingRequestComment) => {
    if (comment.user_id !== currentUserId || request.deleted_at) return
    setEditingCommentId(comment.id)
    setEditingCommentDraft(comment.content)
    setError(null)
  }

  const cancelEditComment = () => {
    setEditingCommentId(null)
    setEditingCommentDraft('')
  }

  const saveEditComment = async () => {
    if (!editingCommentId) return
    const target = comments.find((c) => c.id === editingCommentId)
    if (!target || target.user_id !== currentUserId) return
    const nextContent = editingCommentDraft.trim()
    if (!nextContent) {
      setError('Informe o texto do comentário.')
      return
    }
    if (nextContent === target.content.trim()) {
      cancelEditComment()
      return
    }
    setSavingCommentEdit(true)
    setError(null)
    try {
      const updated = await marketingSolicitacaoService.updateComment(editingCommentId, nextContent)
      setComments((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      cancelEditComment()
      void logUpdate(
        'marketing_request',
        request.id,
        `Editou comentário da solicitação de marketing N° ${request.request_number}`,
        {
          comment_id: target.id,
          content_preview: (target.content || '').slice(0, 200),
        },
        {
          comment_id: updated.id,
          content_preview: nextContent.slice(0, 200),
        }
      )
      void loadHistory(request.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao editar comentário.')
    } finally {
      setSavingCommentEdit(false)
    }
  }

  const removeComment = async (commentId: string) => {
    const target = comments.find((c) => c.id === commentId)
    if (!target || target.user_id !== currentUserId) return
    if (!window.confirm('Excluir este comentário?')) return
    try {
      await marketingSolicitacaoService.deleteComment(commentId)
      setComments((prev) => prev.filter((c) => c.id !== commentId))
      if (editingCommentId === commentId) cancelEditComment()
      void logDelete(
        'marketing_request',
        request.id,
        `Excluiu comentário da solicitação de marketing N° ${request.request_number}`,
        {
          comment_id: commentId,
          content_preview: (target.content || '').slice(0, 200),
          request_number: request.request_number,
        }
      )
      void loadHistory(request.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir comentário.')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-xl flex-col bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <Heading
              as="h3"
              size="xl"
              className={
                request.deleted_at
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-900 dark:text-white'
              }
            >
              Solicitação N° {request.request_number}
            </Heading>
            <Text size="sm" className="mt-1 font-medium text-gray-900 dark:text-white">
              {request.title?.trim() || 'Sem título'}
            </Text>
            <Text size="sm" color="muted" className="mt-1">
              {formatDateTimePtBr(request.created_at)} ·{' '}
              {formatRequesterWithDepartment(request.requester)}
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          <div>
            <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
              Etapa
            </Text>
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{ backgroundColor: request.stage?.color || DEFAULT_STAGE_COLOR }}
              />
              <Text size="sm" className="text-gray-900 dark:text-white">
                {request.stage?.name || '—'}
              </Text>
            </div>
          </div>

          <div>
            <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
              Responsável pela tratativa
            </Text>
            <Text size="sm" className="text-gray-900 dark:text-white">
              {request.handler?.name || '—'}
            </Text>
          </div>

          <div>
            <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
              Prioridade
            </Text>
            {(() => {
              const priority = isMarketingRequestPriority(request.priority)
                ? request.priority
                : 'medium'
              return (
                <span
                  className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[priority]}`}
                >
                  {PRIORITY_LABELS[priority]}
                </span>
              )
            })()}
          </div>

          <div>
            <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
              Para quando
            </Text>
            <Text size="sm" className="text-gray-900 dark:text-white">
              {formatDateOnlyPtBr(request.needed_on)}
            </Text>
          </div>

          {request.closed_at ? (
            <div>
              <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
                Finalização
              </Text>
              <Text size="sm" className="text-gray-900 dark:text-white">
                {formatDateTimePtBr(request.closed_at)}
              </Text>
            </div>
          ) : null}

          <div>
            <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
              Observação geral
            </Text>
            <Text size="sm" className="whitespace-pre-wrap text-gray-800 dark:text-gray-200">
              {request.notes?.trim() || '—'}
            </Text>
          </div>

          {canSeeLinkedDocs && (
            <>
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Text size="sm" className="font-medium text-gray-700 dark:text-gray-300">
                    Orçamentos ({linkedQuotes.length})
                  </Text>
                  <Link
                    to="/marketing/orcamento"
                    className="text-sm text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Abrir orçamentos
                  </Link>
                </div>
                {quotesLoading ? (
                  <LoadingSpinner size="sm" />
                ) : linkedQuotes.length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum orçamento vinculado.
                  </Text>
                ) : (
                  <div className="space-y-2">
                    {linkedQuotes.map((quote) => (
                      <Link
                        key={quote.id}
                        to={`/marketing/orcamento?q=${encodeURIComponent(String(quote.quote_number))}`}
                        className="block rounded-lg border border-gray-200 px-3 py-2 transition-colors hover:border-primary-300 hover:bg-primary-50/50 dark:border-gray-700 dark:hover:border-primary-700 dark:hover:bg-primary-900/20"
                      >
                        <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                          {formatQuoteNumber(quote.quote_number)} · {quote.supplier?.name || '—'}
                        </Text>
                        <Text size="sm" color="muted">
                          {quoteStatusLabel(quote.status)} · {formatCurrency(quoteTotal(quote))}
                        </Text>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Text size="sm" className="font-medium text-gray-700 dark:text-gray-300">
                    Notas fiscais ({linkedInvoices.length})
                  </Text>
                  <Link
                    to="/marketing/notas"
                    className="text-sm text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Abrir notas
                  </Link>
                </div>
                {invoicesLoading ? (
                  <LoadingSpinner size="sm" />
                ) : linkedInvoices.length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhuma nota vinculada.
                  </Text>
                ) : (
                  <div className="space-y-2">
                    {linkedInvoices.map((invoice) => (
                      <Link
                        key={invoice.id}
                        to={`/marketing/notas?q=${encodeURIComponent(invoice.number)}`}
                        className="block rounded-lg border border-gray-200 px-3 py-2 transition-colors hover:border-primary-300 hover:bg-primary-50/50 dark:border-gray-700 dark:hover:border-primary-700 dark:hover:bg-primary-900/20"
                      >
                        <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                          {documentTypeLabel(invoice.document_type)} {invoice.number}
                          {invoice.supplier?.name ? ` · ${invoice.supplier.name}` : ''}
                        </Text>
                        <Text size="sm" color="muted">
                          {invoiceStatusLabel(invoice.status)} · {formatCurrency(invoiceTotal(invoice))}
                        </Text>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Text size="sm" className="font-medium text-gray-700 dark:text-gray-300">
                    Contratos ({linkedContracts.length})
                  </Text>
                  <Link
                    to="/marketing/contratos"
                    className="text-sm text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Abrir contratos
                  </Link>
                </div>
                {contractsLoading ? (
                  <LoadingSpinner size="sm" />
                ) : linkedContracts.length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum contrato vinculado.
                  </Text>
                ) : (
                  <div className="space-y-2">
                    {linkedContracts.map((contract) => {
                      const openTotal = getOpenAmountsTotal(contract.amounts)
                      const q = (contract.contract_number || contract.name || '').trim()
                      return (
                        <Link
                          key={contract.id}
                          to={`/marketing/contratos?q=${encodeURIComponent(q)}`}
                          className="block rounded-lg border border-gray-200 px-3 py-2 transition-colors hover:border-primary-300 hover:bg-primary-50/50 dark:border-gray-700 dark:hover:border-primary-700 dark:hover:bg-primary-900/20"
                        >
                          <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                            {contract.name}
                            {contract.contract_number ? ` · ${contract.contract_number}` : ''}
                          </Text>
                          <Text size="sm" color="muted">
                            {contract.supplier?.name || '—'} · {contractStatusLabel(contract.status)}
                            {openTotal > 0 ? ` · ${formatCurrency(openTotal)}` : ''}
                          </Text>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="space-y-3">
            <Text size="sm" className="font-medium text-gray-700 dark:text-gray-300">
              Itens ({request.items?.length || 0})
            </Text>
            {(request.items || []).map((item, index) => (
              <div
                key={item.id}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
              >
                <Text size="sm" className="mb-3 font-semibold text-gray-900 dark:text-white">
                  {ITEM_TYPE_LABELS[item.item_type]} #{index + 1}
                </Text>
                <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(item.details || {})
                    .filter(([key]) => key !== 'item_id')
                    .map(([key, value]) => (
                      <div key={key}>
                        <dt className="text-xs text-gray-500 dark:text-gray-400">
                          {DETAIL_LABELS[key] || key}
                        </dt>
                        <dd className="text-sm text-gray-900 dark:text-white">
                          {formatDetailValue(key, value)}
                        </dd>
                      </div>
                    ))}
                </dl>
              </div>
            ))}
          </div>

          <div>
            <Text size="sm" className="mb-2 font-medium text-gray-700 dark:text-gray-300">
              Anexos ({attachments.length})
            </Text>
            {attachments.length === 0 ? (
              <Text size="sm" color="muted">
                Nenhum anexo.
              </Text>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {attachments.map((att) => {
                  const url = signedUrls[att.id]
                  const image = isImageAttachment(att.mime_type, att.file_name)
                  return (
                    <a
                      key={att.id}
                      href={url || undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="relative aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-900"
                      title={att.file_name}
                    >
                      {image && url ? (
                        <img src={url} alt={att.file_name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center">
                          <DocumentTextIcon className="h-7 w-7 text-primary-500 dark:text-primary-400" />
                          <p className="line-clamp-2 break-all text-[10px] text-gray-600 dark:text-gray-300">
                            {att.file_name}
                          </p>
                        </div>
                      )}
                    </a>
                  )
                })}
              </div>
            )}
          </div>

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Comentários {!commentsLoading && comments.length > 0 && `(${comments.length})`}
            </h3>

            {commentsLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : comments.length === 0 ? (
              <p className="mb-3 text-sm italic text-gray-400">Nenhum comentário ainda.</p>
            ) : (
              <ul className="mb-4 space-y-3">
                {comments.map((c) => {
                  const isOwner = c.user_id === currentUserId
                  const isEditing = editingCommentId === c.id
                  return (
                    <li key={c.id} className="flex gap-3">
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold uppercase text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                        {(c.author?.name || 'U').charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                            {c.author?.name || 'Usuário'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(c.created_at).toLocaleString('pt-BR')}
                          </span>
                          {isCommentEdited(c) && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">(editado)</span>
                          )}
                          {isOwner && !request.deleted_at && !isEditing && (
                            <div className="ml-auto flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => startEditComment(c)}
                                className="text-xs text-gray-400 hover:text-primary-600 dark:text-gray-500 dark:hover:text-primary-400"
                                title="Editar comentário"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => void removeComment(c.id)}
                                className="text-xs text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                                title="Excluir comentário"
                              >
                                Excluir
                              </button>
                            </div>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="mt-2 space-y-2">
                            <textarea
                              rows={3}
                              value={editingCommentDraft}
                              onChange={(e) => setEditingCommentDraft(e.target.value)}
                              disabled={savingCommentEdit}
                              className="w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                            />
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={cancelEditComment}
                                disabled={savingCommentEdit}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                variant="solid"
                                size="sm"
                                onClick={() => void saveEditComment()}
                                disabled={savingCommentEdit || !editingCommentDraft.trim()}
                              >
                                {savingCommentEdit ? 'Salvando...' : 'Salvar'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="mt-0.5 whitespace-pre-line text-sm text-gray-900 dark:text-white">
                            {c.content}
                          </p>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}

            {!request.deleted_at && (
              <div className="flex flex-col gap-2">
                <textarea
                  ref={textareaRef}
                  rows={3}
                  placeholder="Escreva um comentário..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void submitComment()
                  }}
                  className="w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
                <div className="flex items-center justify-end gap-2">
                  <span className="hidden text-xs text-gray-400 sm:inline">Ctrl+Enter para enviar</span>
                  <Button
                    variant="solid"
                    size="sm"
                    onClick={() => void submitComment()}
                    disabled={submittingComment || !newComment.trim()}
                  >
                    {submittingComment ? 'Enviando...' : 'Comentar'}
                  </Button>
                </div>
              </div>
            )}
          </section>

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Histórico de alterações
            </h3>
            {historyLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm italic text-gray-400">Nenhuma alteração registrada.</p>
            ) : (
              <ul className="space-y-2">
                {history.map((h) => (
                  <li key={h.id} className="border-l-2 border-gray-200 pl-3 text-sm dark:border-gray-700">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(h.changed_at).toLocaleString('pt-BR')}
                      {h.changer?.name && <> · {h.changer.name}</>}
                    </div>
                    <div className="text-gray-900 dark:text-white">
                      <span className="font-medium">{FIELD_LABELS[h.field_changed] || h.field_changed}</span>:{' '}
                      <span className="text-red-500 line-through dark:text-red-400">
                        {formatHistoryValue(h.field_changed, h.old_value, historyCtx)}
                      </span>
                      {' → '}
                      <span className="text-green-600 dark:text-green-400">
                        {formatHistoryValue(h.field_changed, h.new_value, historyCtx)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex min-w-0 items-center gap-3">
            {canDeleteRequest && (
              <Button
                variant="outline"
                className="!text-red-600 hover:!bg-red-50 dark:!text-red-400 dark:hover:!bg-red-950/40 !ring-red-200 dark:!ring-red-900/60"
                onClick={() => {
                  void onDelete(request.id)
                }}
              >
                Excluir
              </Button>
            )}
            {canEditRequest && (
              <Button variant="solid" onClick={() => onEdit(request)}>
                Editar
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SolicitacaoDetailDrawer
